import React, { useEffect, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Card, Typography, Button, Space, Alert } from 'antd';
import { CheckCircleOutlined } from '@ant-design/icons';
import { useUser } from '../../contexts/UserContext';
import { bootstrapCompanyArtifacts } from '../../services/api/onboardingService';
import { resolveWorkspaceCompanyId } from '../../utils/resolveWorkspaceCompanyId';

const { Title, Paragraph } = Typography;

/**
 * Post-payment / post-booking confirmation (Screen 4 intake spec).
 */
export const FoundationConfirmationPage: React.FC = () => {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const fromStripe = params.get('checkout') === 'success';
  const {
    user,
    selectedCompany,
    availableCompanies: companiesList = [],
    loadUserCompanies,
    isAuthenticated,
  } = useUser();

  const companyId = useMemo(
    () => resolveWorkspaceCompanyId(user?.companyId, selectedCompany?.id, companiesList),
    [user?.companyId, selectedCompany?.id, companiesList]
  );

  useEffect(() => {
    if (!user?.id || !isAuthenticated) return;
    void loadUserCompanies();
  }, [user?.id, isAuthenticated, loadUserCompanies]);

  /** ISSUE-244: idempotent skeleton artifacts so Control Bridge intake has a graph to attach to. */
  useEffect(() => {
    if (!fromStripe || !companyId) return;
    void bootstrapCompanyArtifacts(companyId).catch(() => {
      /* Non-fatal: webhook may still run; user can retry from ops. */
    });
  }, [fromStripe, companyId]);

  return (
    <div style={{ maxWidth: 560, margin: '48px auto', padding: 24 }}>
      <Card>
        <Space direction="vertical" size="large" style={{ width: '100%' }}>
          <CheckCircleOutlined style={{ fontSize: 48, color: '#52c41a' }} />
          <div>
            <Title level={3} style={{ marginTop: 0 }}>
              {fromStripe ? 'Payment received' : "You're on the calendar"}
            </Title>
            {fromStripe && (
              <Alert
                type="success"
                showIcon
                style={{ marginBottom: 16 }}
                message="Stripe checkout completed"
                description="Your foundation purchase is being recorded. You'll receive scheduling details from the team."
              />
            )}
            <Paragraph>
              Your Foundation Session is being scheduled with our team. You&apos;ll receive details on what to bring
              and what to expect.
            </Paragraph>
            <Paragraph>
              Payment records foundation billing only. Your company model unlocks in Control Bridge after the
              facilitated session is written and imported into the platform (workspace slug plus company-model
              artifact items — not platform-init seed items alone). Billing complete does not mean the model is
              ready. Platform gates are checked separately before Bridge Session create.
            </Paragraph>
            {user?.onboarding?.modelReady ? (
              <Alert
                type="success"
                showIcon
                style={{ marginBottom: 16 }}
                message="Company model ready"
                description="At least one of your companies has a workspace slug and imported company-model items."
              />
            ) : user?.onboarding?.foundationComplete ? (
              <Alert
                type="info"
                showIcon
                style={{ marginBottom: 16 }}
                message="Billing recorded — model not ready yet"
                description="Foundation purchase is on file. Control Bridge work queues unlock after session import into Postgres."
              />
            ) : null}
            <Paragraph type="secondary">
              Backend invoice and subscription state follow ISSUE-246 / ISSUE-264; Stripe webhooks complete the
              purchase record. Model readiness is a separate signal (`model_ready` on auth context).
            </Paragraph>
          </div>
          <Button type="primary" size="large" block onClick={() => navigate('/dashboard')}>
            Open Control Bridge
          </Button>
          <Button block onClick={() => navigate('/onboarding/prospector')}>
            Return to discovery chat
          </Button>
        </Space>
      </Card>
    </div>
  );
};
